import { Box, Typography } from "@mui/material";

// Shared panel-local heading that replaces the global TopNav on routes where
// it's hidden (Home.jsx's NO_TOP_NAV_ROUTES) — pattern first built inline in
// InboxSettings.jsx, extracted here (2026-09-01). Height matches TopNav's
// real measured total (minHeight + padding + border — see ui-gotchas memory)
// so hiding the global bar doesn't shift anything.
//
// This is one of the app's TWO going-forward navbar types (the old global
// TopNav.jsx is the third, legacy one — left as-is for pages not yet
// rebuilt). For any NEW page: (a) add its route to Home.jsx's
// NO_TOP_NAV_ROUTES, (b) render this component instead of <TopNav/>, picking
// whichever variant fits — never hand-copy this markup into a new file.
//   - variant="full"   — AI Agents' style: spans the full page width.
//   - variant="narrow" — Inbox's style: capped to a side-panel's width.
//
// NOTE: InboxSettings.jsx still has its OWN inline copy of this markup (the
// original the "narrow" variant is modeled on), not yet migrated to use this
// component — left untouched on purpose (Rahim: don't touch the Inbox
// heading). Migrate it to this component only if asked.
// `leading` — an optional element (e.g. a back IconButton) rendered to the
// LEFT of the title, for views nested one level below the panel's main
// section (e.g. AI Agents' create/edit-agent view). Absent by default so
// existing callers (Inbox-style, AI Agents' list view) are unaffected.
//
// `icon` / `subtitle` — added 2026-09-10 for Settings' content header: `icon`
// sits between `leading` and the title (a small page-icon, distinct from
// `leading`'s back-arrow role); `subtitle` is an optional second line under
// the title describing what the page does. Both default to absent, which
// renders identically to before (single 1.3rem title line, fixed height) --
// existing callers (Inbox-style, AI Agents) are unaffected.
//
// SUBTITLE_HEIGHT is exported so a sibling PanelHeading with NO subtitle of
// its own (e.g. Settings' narrow sidebar header) can still be forced to this
// same fixed height when it sits next to one that does have a subtitle --
// two independent auto-heights (one from padding, one fixed) never land on
// the exact same pixel value, which left the two headers' bottom borders
// visibly misaligned (Rahim, 2026-09-10: "match the lines perfect").
export const SUBTITLE_HEIGHT = { xs: "58px", sm: "68px", md: "70px", lg: "70px", xl: "70px" };

const PanelHeading = ({ title, subtitle, icon, action, leading, variant = "full", sx }) => (
  <Box
    sx={{
      flexShrink: 0,
      height: subtitle
        ? SUBTITLE_HEIGHT
        : { xs: "49px", sm: "61px", md: "63px", lg: "63px", xl: "63px" },
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      pl: "18px",
      pr: "16px",
      borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
      // Matches Inbox's own settings-panel width (Inbox.jsx's InboxSettings
      // wrapper Box, width:"280px") so a "narrow" heading sits flush with
      // whatever narrow panel it's placed above.
      ...(variant === "narrow" ? { maxWidth: "280px" } : null),
      ...sx,
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      {leading}
      {icon && (
        <Box sx={{ width: 22, height: 22, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          {icon}
        </Box>
      )}
      <Box>
        <Typography sx={{ fontSize: subtitle ? "1.05rem" : "1.3rem", fontWeight: 600, color: "text.primary" }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography sx={{ fontSize: "0.8125rem", color: "text.secondary", mt: "1px" }}>
            {subtitle}
          </Typography>
        )}
      </Box>
    </Box>
    {action}
  </Box>
);

export default PanelHeading;
